import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import { Description, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Switch, SwitchField } from "src/components/base/switch";
import { ApiInternal } from "src/api/api_internal";
import { SimpleGroup } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link UpdateGroupDialog}
 */
export type UpdateGroupDialogProps = {
    /** Callback that should be called when closing the popup */
    onClose: () => void;

    /** Callback that should be called when the group was updated */
    onUpdate: () => void;

    /** The group to update */
    group: SimpleGroup;
};

/**
 * The dialog to update a group
 */
export default function UpdateGroupDialog(props: UpdateGroupDialogProps) {
    const [t] = useTranslation("dialog-update-group");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            description: props.group.description,
            internet: props.group.internet,
        },
        onSubmit: async ({ value }) => {
            await ApiInternal.groups.update(props.group.uuid, {
                description: value.description,
                internet: value.internet,
            });

            props.onUpdate();
        },
    });

    return (
        <Dialog onClose={props.onClose} open={true}>
            <Form onSubmit={form.handleSubmit}>
                <Fieldset>
                    <DialogTitle>
                        {t("heading.change-description", {
                            group: props.group.name,
                        })}
                    </DialogTitle>

                    <FieldGroup>
                        <form.Field name={"description"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.description")}</Label>
                                    <Input
                                        autoComplete={"off"}
                                        autoFocus={true}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"internet"}>
                            {(fieldApi) => (
                                <SwitchField>
                                    <Label>{t("label.allow-exit-nodes")}</Label>
                                    <Description>{t("description.allow-exit-nodes")}</Description>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e)}
                                    />
                                </SwitchField>
                            )}
                        </form.Field>

                        <DialogActions>
                            <Button
                                plain={true}
                                onClick={() => {
                                    props.onUpdate();
                                    form.reset();
                                }}
                            >
                                {tg("button.cancel")}
                            </Button>
                            <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                        </DialogActions>
                    </FieldGroup>
                </Fieldset>
            </Form>
        </Dialog>
    );
}
